import React, { useState, createContext, useContext, useEffect, useCallback, useRef } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Header, Sidebar, Footer, TableOfContents } from './components'
import { HomePage } from './pages/HomePage'
import { FAQPage } from './pages/FAQPage'
import { LocaleProvider, useLocale } from './i18n'

// Mobile Sidebar Context
interface SidebarContextType {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextType | null>(null)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider')
  }
  return context
}

function MobileTOC({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation()

  useEffect(() => {
    if (isOpen) onClose()
  }, [location.pathname])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <>
      <div className={`toc-mobile-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} aria-hidden="true" />
      <div className={`toc-mobile ${isOpen ? 'open' : ''}`} role="dialog" aria-modal="true" aria-label="Table of contents">
        <TableOfContents />
      </div>
    </>
  )
}

function MobileSidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation()
  const { t } = useLocale()

  useEffect(() => {
    if (isOpen) onClose()
  }, [location.pathname])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <>
      <div className={`sidebar-mobile-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} aria-hidden="true" />
      <div className={`sidebar-mobile ${isOpen ? 'open' : ''}`} role="dialog" aria-modal="true" aria-label={t('sidebar.mobileAria')}>
        <Sidebar className="sidebar-mobile-content" />
      </div>
    </>
  )
}

function DocsLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [tocOpen, setTocOpen] = useState(false)
  const location = useLocation()
  const footerRef = useRef<HTMLDivElement>(null)
  const { t } = useLocale()

  useEffect(() => {
    document.body.classList.add('no-transitions')
    const timer = setTimeout(() => {
      document.body.classList.remove('no-transitions')
    }, 50)
    return () => {
      clearTimeout(timer)
      document.body.classList.remove('no-transitions')
    }
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => {
      if (!footerRef.current) return
      const footerRect = footerRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const headerHeight = 56
      const footerVisibleHeight = Math.max(0, viewportHeight - footerRect.top)
      const sidebarHeight = viewportHeight - headerHeight - footerVisibleHeight
      document.documentElement.style.setProperty('--sidebar-height', `${Math.max(0, sidebarHeight)}px`)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  useEffect(() => {
    let touchStartX = 0
    let touchStartY = 0
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
    }
    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const deltaX = touchEndX - touchStartX
      const deltaY = Math.abs(touchEndY - touchStartY)
      const screenWidth = window.innerWidth
      if (deltaY > 100) return
      if (deltaX > 50) {
        if (tocOpen) setTocOpen(false)
        else if (touchStartX < 50 && !sidebarOpen) setSidebarOpen(true)
      }
      if (deltaX < -50) {
        if (sidebarOpen) setSidebarOpen(false)
        else if (touchStartX > screenWidth - 50 && !tocOpen) setTocOpen(true)
      }
    }
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [tocOpen, sidebarOpen])

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => {
      if (!prev) setTocOpen(false)
      return !prev
    })
  }, [])

  const closeSidebar = useCallback(() => setSidebarOpen(false), [])

  const openToc = useCallback(() => {
    setSidebarOpen(false)
    setTocOpen(true)
  }, [])

  const sidebarContextValue = {
    isOpen: sidebarOpen,
    setIsOpen: setSidebarOpen,
    toggle: toggleSidebar,
  }

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      <div className="docs-page-layout">
        <Header onMenuClick={toggleSidebar} isSidebarOpen={sidebarOpen} />
        <div className="docs-page-wrapper">
          <Sidebar />
          <MobileSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          <main className="docs-main" role="main" aria-label={t('doc.mainAria')}>
            {children}
          </main>
          <TableOfContents />
          <MobileTOC isOpen={tocOpen} onClose={() => setTocOpen(false)} />
          <button className="toc-mobile-toggle" onClick={openToc} aria-label={t('doc.tocOpen')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
        <div ref={footerRef} className="docs-footer-wrapper">
          <Footer />
        </div>
      </div>
    </SidebarContext.Provider>
  )
}

export default function App() {
  return (
    <HelmetProvider>
      <LocaleProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/de" element={<HomePage />} />

          {/* Dynamic FAQ routes */}
          <Route path="/faq/:section/:slug" element={<DocsLayout><FAQPage /></DocsLayout>} />
          <Route path="/de/faq/:section/:slug" element={<DocsLayout><FAQPage /></DocsLayout>} />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </LocaleProvider>
    </HelmetProvider>
  )
}
